import apiService from './api';
import type { LotSprayHistoryEntry } from '../types/spray';
import type {
  NurseryTreeType,
  NurseryOrderStatus,
  OrderCancellation, OrderReprice, OrderRepriceResult,
  OrderCancelResult,
  NurseryRow,
  NurseryStockItem,
  NurseryProduct,
  ProductPayload,
  NurseryBatch,
  NurseryGrowing,
  NurserySourceRecord,
  NurseryGrading,
  NurseryOrder,
  OrderDespatchPayload,
  NurseryPickersResponse,
  NurseryOrderLine,
  OrderSheet,
  OrderSheetLineResult,
  NurseryAllocation,
  NurseryLoad,
  CreateLoadPayload,
  UpdateLoadPayload,
  DespatchPlanner,
  BackorderList,
  BackorderCrossDockResult,
  LoadShortfall,
  DespatchChoice,
  DespatchResult,
  NurseryTransfer,
  CreateTransferPayload,
  DespatchPlace,
  DespatchPlacePayload,
  AutoAllocateResult,
  NoticeKind,
  NoticePreview,
  NoticeResult,
  SupplierManifest,
  SupplierManifestHeader,
  PickLoadsResponse,
  PickList,
  PickAlternative,
  PickMode,
  DespatchSettings,
  PickShortReason,
  NurseryInvoice,
  AvailableStock,
  CreateRowPayload,
  BulkCreateRowsPayload,
  CreateStockItemPayload,
  StockItemKind,
  RetailCategory,
  StockItemBarcode,
  CreateBatchPayload,
  CreateGrowingPayload,
  CreateSourceRecordPayload,
  RecordGradingPayload,
  CreateOrderPayload,
  OrderLinePayload,
  AllocatePayload,
  CreateInvoicePayload,
  CreateTreeTypePayload,
  NurseryCustomerTier,
  CreateCustomerTierPayload,
  TradeAccountRequest,
  ReviewTradeAccountPayload,
  NurseryDeliveryZone,
  CreateDeliveryZonePayload,
  NurseryCustomer,
  CreateCustomerPayload,
  NurseryPriceList,
  CreatePriceListPayload,
  LicensedCultivar,
  WebsiteSignup,
  LinkWebsiteSignupPayload,
  TreeTypeUpdatePayload,
  TreeTypeGrade,
  GradeDiscount,
  TreeTypeGradesResponse,
  ForecastResponse,
  NurseryLocation,
  PlaceFilter,
  CreateLocationPayload,
  CreateRowSlotsPayload,
  NurseryBlockLayout,
  BlockLayoutPayload,
  NurseryLot,
  CreateLotPayload,
  NurseryMovement,
  ApplyMovementPayload,
  NurseryLotLineage,
  NurseryProductionRun,
  CreateProductionRunPayload,
  ProductionBoardFilters,
  NurseryInventoryRow,
  NurseryDashboardStatsV2,
  NurseryCultivarInfo,
  CultivarInfoSubject,
  LotStage,
  LotChangeResult,
} from '../types/nursery';
import type { PriceQuote } from '../types/nurseryAccounts';
import type { ApiResponse } from '../types';

class NurseryService {

  // ── Tree Types ──

  async getTreeTypes(stockType?: 'B' | 'P'): Promise<ApiResponse<NurseryTreeType[]>> {
    const q = stockType ? `?stock_type=${stockType}` : '';
    return apiService.get(`/nursery/tree-types${q}`);
  }

  async createTreeType(data: CreateTreeTypePayload): Promise<ApiResponse<NurseryTreeType>> {
    return apiService.post('/nursery/tree-types', data);
  }

  // ── Batches ──

  async getBatches(filters?: { status?: string; stock_type?: string }): Promise<ApiResponse<NurseryBatch[]>> {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.stock_type) params.set('stock_type', filters.stock_type);
    const q = params.toString() ? `?${params}` : '';
    return apiService.get(`/nursery/batches${q}`);
  }

  async getBatch(id: number): Promise<ApiResponse<NurseryBatch>> {
    return apiService.get(`/nursery/batches/${id}`);
  }

  async createBatch(data: CreateBatchPayload): Promise<ApiResponse<NurseryBatch>> {
    return apiService.post('/nursery/batches', data);
  }

  async updateBatch(id: number, data: Partial<NurseryBatch>): Promise<ApiResponse<NurseryBatch>> {
    return apiService.put(`/nursery/batches/${id}`, data);
  }

  // ── Rows ──

  async getRows(filters?: { block_id?: number; farm_id?: number; active?: boolean }): Promise<ApiResponse<NurseryRow[]>> {
    const params = new URLSearchParams();
    if (filters?.block_id) params.set('block_id', String(filters.block_id));
    if (filters?.farm_id) params.set('farm_id', String(filters.farm_id));
    if (filters?.active !== undefined) params.set('active', String(filters.active));
    const q = params.toString() ? `?${params}` : '';
    return apiService.get(`/nursery/rows${q}`);
  }

  async getRow(id: number): Promise<ApiResponse<NurseryRow>> {
    return apiService.get(`/nursery/rows/${id}`);
  }

  async createRow(data: CreateRowPayload): Promise<ApiResponse<NurseryRow>> {
    return apiService.post('/nursery/rows', data);
  }

  async bulkCreateRows(data: BulkCreateRowsPayload): Promise<ApiResponse<NurseryRow[]>> {
    return apiService.post('/nursery/rows/bulk', data);
  }

  async updateRow(id: number, data: Partial<NurseryRow>): Promise<ApiResponse<NurseryRow>> {
    return apiService.put(`/nursery/rows/${id}`, data);
  }

  async deleteRow(id: number): Promise<ApiResponse<void>> {
    return apiService.delete(`/nursery/rows/${id}`);
  }

  // ── Stock Items ──

  async getStockItems(filters?: {
    kind?: StockItemKind;
    category_id?: number;
    cultivar_id?: number;
    rootstock_id?: number;
    tree_type_id?: number;
    stock_type?: string;
    active?: boolean;
  }): Promise<ApiResponse<NurseryStockItem[]>> {
    const params = new URLSearchParams();
    if (filters?.kind) params.set('kind', filters.kind);
    if (filters?.category_id) params.set('category_id', String(filters.category_id));
    if (filters?.cultivar_id) params.set('cultivar_id', String(filters.cultivar_id));
    if (filters?.rootstock_id) params.set('rootstock_id', String(filters.rootstock_id));
    if (filters?.tree_type_id) params.set('tree_type_id', String(filters.tree_type_id));
    if (filters?.stock_type) params.set('stock_type', filters.stock_type);
    if (filters?.active !== undefined) params.set('active', String(filters.active));
    const q = params.toString() ? `?${params}` : '';
    return apiService.get(`/nursery/stock-items${q}`);
  }

  async getStockItem(id: number): Promise<ApiResponse<NurseryStockItem>> {
    return apiService.get(`/nursery/stock-items/${id}`);
  }

  async createStockItem(data: CreateStockItemPayload): Promise<ApiResponse<NurseryStockItem>> {
    return apiService.post('/nursery/stock-items', data);
  }

  async updateStockItem(id: number, data: Partial<NurseryStockItem>): Promise<ApiResponse<NurseryStockItem>> {
    return apiService.put(`/nursery/stock-items/${id}`, data);
  }

  /** The item a scanned code names: its own barcode first, then a supplier's. 404 when none. */
  async findStockItemByBarcode(code: string): Promise<ApiResponse<NurseryStockItem & { matched_supplier_id: number | null }>> {
    return apiService.get(`/nursery/stock-items/by-barcode/${encodeURIComponent(code)}`);
  }

  /** Suppliers' tag barcodes for an item. */
  async getStockItemBarcodes(id: number): Promise<ApiResponse<StockItemBarcode[]>> {
    return apiService.get(`/nursery/stock-items/${id}/barcodes`);
  }

  /** Replace an item's supplier barcodes. */
  async setStockItemBarcodes(id: number, barcodes: StockItemBarcode[]): Promise<ApiResponse<StockItemBarcode[]>> {
    return apiService.put(`/nursery/stock-items/${id}/barcodes`, {
      barcodes: barcodes.map((b) => ({ barcode: b.barcode, supplier_id: b.supplier_id })),
    });
  }

  // ── Shop categories ──

  async getRetailCategories(includeInactive = false): Promise<ApiResponse<RetailCategory[]>> {
    return apiService.get(`/nursery/retail-categories${includeInactive ? '?include_inactive=true' : ''}`);
  }

  async createRetailCategory(data: { name: string; sort_order?: number }): Promise<ApiResponse<RetailCategory>> {
    return apiService.post('/nursery/retail-categories', data);
  }

  async updateRetailCategory(
    id: number,
    data: Partial<Pick<RetailCategory, 'name' | 'sort_order' | 'active'>>,
  ): Promise<ApiResponse<RetailCategory>> {
    return apiService.put(`/nursery/retail-categories/${id}`, data);
  }

  // Per-tier visibility allowlist for a stock item (empty => visible to all tiers).
  async getStockItemTierRules(id: number): Promise<ApiResponse<number[]>> {
    return apiService.get(`/nursery/stock-items/${id}/tier-rules`);
  }

  async setStockItemTierRules(id: number, tierIds: number[]): Promise<ApiResponse<number[]>> {
    return apiService.put(`/nursery/stock-items/${id}/tier-rules`, { tier_ids: tierIds });
  }

  // ── Umbrella products: one name and price over several stock items ──

  async getProducts(filters?: { active?: boolean }): Promise<ApiResponse<NurseryProduct[]>> {
    const q = filters?.active !== undefined ? `?active=${filters.active}` : '';
    return apiService.get(`/nursery/products${q}`);
  }

  async getProduct(id: number): Promise<ApiResponse<NurseryProduct>> {
    return apiService.get(`/nursery/products/${id}`);
  }

  async createProduct(data: ProductPayload): Promise<ApiResponse<NurseryProduct>> {
    return apiService.post('/nursery/products', data);
  }

  async updateProduct(id: number, data: Partial<ProductPayload>): Promise<ApiResponse<NurseryProduct>> {
    return apiService.put(`/nursery/products/${id}`, data);
  }

  async getProductTierRules(id: number): Promise<ApiResponse<number[]>> {
    return apiService.get(`/nursery/products/${id}/tier-rules`);
  }

  async setProductTierRules(id: number, tierIds: number[]): Promise<ApiResponse<number[]>> {
    return apiService.put(`/nursery/products/${id}/tier-rules`, { tier_ids: tierIds });
  }

  // ── Website customer signups (legacy backfill worklist) ──
  async getWebsiteSignups(status?: string): Promise<ApiResponse<WebsiteSignup[]>> {
    const q = status ? `?status=${encodeURIComponent(status)}` : '';
    return apiService.get(`/nursery/website-signups${q}`);
  }

  async linkWebsiteSignup(id: number, data: LinkWebsiteSignupPayload): Promise<ApiResponse<WebsiteSignup>> {
    return apiService.post(`/nursery/website-signups/${id}/link`, data);
  }

  // ── Growing ──

  async getGrowingRecords(filters?: {
    status?: string;
    batch_id?: number;
    stock_item_id?: number;
    row_id?: number;
    block_id?: number;
    stock_type?: string;
    active_only?: boolean;
  }): Promise<ApiResponse<NurseryGrowing[]>> {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.batch_id) params.set('batch_id', String(filters.batch_id));
    if (filters?.stock_item_id) params.set('stock_item_id', String(filters.stock_item_id));
    if (filters?.row_id) params.set('row_id', String(filters.row_id));
    if (filters?.block_id) params.set('block_id', String(filters.block_id));
    if (filters?.stock_type) params.set('stock_type', filters.stock_type);
    if (filters?.active_only) params.set('active_only', 'true');
    const q = params.toString() ? `?${params}` : '';
    return apiService.get(`/nursery/growing${q}`);
  }

  async getGrowing(id: number): Promise<ApiResponse<NurseryGrowing>> {
    return apiService.get(`/nursery/growing/${id}`);
  }

  async createGrowing(data: CreateGrowingPayload): Promise<ApiResponse<NurseryGrowing>> {
    return apiService.post('/nursery/growing', data);
  }

  async updateGrowing(id: number, data: Partial<NurseryGrowing>): Promise<ApiResponse<NurseryGrowing>> {
    return apiService.put(`/nursery/growing/${id}`, data);
  }

  async updateGrowingStatus(id: number, status: string, data?: { budded_date?: string; dig_date?: string }): Promise<ApiResponse<NurseryGrowing>> {
    return apiService.post(`/nursery/growing/${id}/status`, { status, ...data });
  }

  async recordDig(id: number, digDate: string): Promise<ApiResponse<NurseryGrowing>> {
    return apiService.post(`/nursery/growing/${id}/dig`, { dig_date: digDate });
  }

  async recordContainerSale(id: number, quantity: number, saleDate?: string): Promise<ApiResponse<NurseryGrowing>> {
    return apiService.post(`/nursery/growing/${id}/sell`, { quantity, sale_date: saleDate });
  }

  // ── Source Records ──

  async getSourceRecords(growingId: number): Promise<ApiResponse<NurserySourceRecord[]>> {
    return apiService.get(`/nursery/growing/${growingId}/sources`);
  }

  async createSourceRecord(growingId: number, data: CreateSourceRecordPayload): Promise<ApiResponse<NurserySourceRecord>> {
    return apiService.post(`/nursery/growing/${growingId}/sources`, data);
  }

  // ── Gradings ──

  async getGradings(growingId: number): Promise<ApiResponse<NurseryGrading[]>> {
    return apiService.get(`/nursery/growing/${growingId}/gradings`);
  }

  async recordGrading(growingId: number, data: RecordGradingPayload): Promise<ApiResponse<NurseryGrading>> {
    return apiService.post(`/nursery/growing/${growingId}/gradings`, data);
  }

  // ── Orders ──

  async getOrders(filters?: { status?: string; customer_org_id?: number; customer_id?: number; backorders?: boolean }): Promise<ApiResponse<NurseryOrder[]>> {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.backorders) params.set('backorders', 'true');
    if (filters?.customer_id) params.set('customer_id', String(filters.customer_id));
    if (filters?.customer_org_id) params.set('customer_org_id', String(filters.customer_org_id));
    const q = params.toString() ? `?${params}` : '';
    return apiService.get(`/nursery/orders${q}`);
  }

  async getOrder(id: number): Promise<ApiResponse<NurseryOrder>> {
    return apiService.get(`/nursery/orders/${id}`);
  }

  async createOrder(data: CreateOrderPayload): Promise<ApiResponse<NurseryOrder>> {
    return apiService.post('/nursery/orders', data);
  }

  async updateOrder(id: number, data: Partial<NurseryOrder> | OrderDespatchPayload): Promise<ApiResponse<NurseryOrder>> {
    return apiService.put(`/nursery/orders/${id}`, data);
  }

  /** What cancelling would release, withdraw and refund, and what stops it. */
  async getOrderCancellation(id: number): Promise<ApiResponse<OrderCancellation>> {
    return apiService.get(`/nursery/orders/${id}/cancellation`);
  }

  async getOrderReprice(id: number): Promise<ApiResponse<OrderReprice>> {
    return apiService.get(`/nursery/orders/${id}/reprice`);
  }

  async repriceOrder(id: number, all: boolean): Promise<ApiResponse<OrderRepriceResult>> {
    return apiService.post(`/nursery/orders/${id}/reprice`, { all });
  }

  async cancelOrder(id: number, reason: string | null): Promise<ApiResponse<OrderCancelResult>> {
    // A card refund goes to Stripe after the cancel commits.
    return apiService.post(`/nursery/orders/${id}/cancel`, { reason }, undefined, { timeout: 30000 });
  }

  async addOrderLine(orderId: number, data: OrderLinePayload): Promise<ApiResponse<NurseryOrderLine>> {
    return apiService.post(`/nursery/orders/${orderId}/lines`, data);
  }

  async updateOrderLine(orderId: number, lineId: number, data: Partial<NurseryOrderLine>): Promise<ApiResponse<NurseryOrderLine>> {
    return apiService.put(`/nursery/orders/${orderId}/lines/${lineId}`, data);
  }

  async removeOrderLine(orderId: number, lineId: number): Promise<ApiResponse<void>> {
    return apiService.delete(`/nursery/orders/${orderId}/lines/${lineId}`);
  }

  // ── The order sheet: everything sold, with its stock, one quantity each ──

  /** With a customer (and no order yet), each row carries that customer's prices. */
  async getOrderSheet(filters?: { farmId?: number | null; orderId?: number | null; customerId?: number | null }): Promise<ApiResponse<OrderSheet>> {
    const params = new URLSearchParams();
    if (filters?.farmId) params.set('farm_id', String(filters.farmId));
    if (filters?.orderId) params.set('order_id', String(filters.orderId));
    else if (filters?.customerId) params.set('customer_id', String(filters.customerId));
    const q = params.toString() ? `?${params}` : '';
    return apiService.get(`/nursery/order-sheet${q}`);
  }

  /** What staff read about a stock item's cultivar and rootstock, or a product's members. */
  async getCultivarInfo(subject: CultivarInfoSubject): Promise<ApiResponse<{ cultivars: NurseryCultivarInfo[] }>> {
    const q = 'product_id' in subject ? `product_id=${subject.product_id}` : `stock_item_id=${subject.stock_item_id}`;
    return apiService.get(`/nursery/cultivar-info?${q}`);
  }

  /** Make the order's line for one row match a quantity; 0 removes it. */
  async setOrderSheetLine(
    orderId: number,
    subject: { stock_item_id: number } | { product_id: number },
    quantity: number,
  ): Promise<ApiResponse<OrderSheetLineResult>> {
    return apiService.put(`/nursery/orders/${orderId}/sheet-line`, { ...subject, quantity });
  }

  // ── Customer Orders (no nursery license needed) ──

  async getMyOrders(): Promise<ApiResponse<NurseryOrder[]>> {
    return apiService.get('/nursery/my-orders');
  }

  async getMyOrder(id: number): Promise<ApiResponse<NurseryOrder>> {
    return apiService.get(`/nursery/my-orders/${id}`);
  }

  // ── Order pickers ──

  async getPickers(): Promise<ApiResponse<NurseryPickersResponse>> {
    return apiService.get('/nursery/pickers');
  }

  async setPickerFarms(userId: number, farmIds: number[]): Promise<ApiResponse<{ user_id: number; farm_ids: number[] }>> {
    return apiService.put(`/nursery/pickers/${userId}/farms`, { farm_ids: farmIds });
  }

  // ── Allocations ──

  /**
   * Ready lots to allocate. Given an order line, every stock item it may be
   * filled from (an umbrella product's members included); given a stock item,
   * just that one.
   */
  async getAvailableStock(target: number | { orderLineId: number }): Promise<ApiResponse<AvailableStock[]>> {
    const params = typeof target === 'number'
      ? new URLSearchParams({ stock_item_id: String(target) })
      : new URLSearchParams({ order_line_id: String(target.orderLineId) });
    return apiService.get(`/nursery/available-stock?${params}`);
  }

  async allocate(data: AllocatePayload): Promise<ApiResponse<NurseryAllocation>> {
    return apiService.post('/nursery/allocations', data);
  }

  async deallocate(id: number): Promise<ApiResponse<void>> {
    return apiService.delete(`/nursery/allocations/${id}`);
  }

  // ── Despatch, paperwork, transfers and despatchable places ──

  async getLoadShortfalls(loadId: number): Promise<ApiResponse<LoadShortfall[]>> {
    return apiService.get(`/nursery/loads/${loadId}/shortfalls`);
  }

  async despatchLoad(loadId: number, choices: DespatchChoice[]): Promise<ApiResponse<DespatchResult>> {
    // A big load takes longer than the 10 s default: every pick, backorder and
    // invoice is written in one transaction. Giving up early leaves the page
    // saying nothing while the server finishes the despatch.
    return apiService.post(`/nursery/loads/${loadId}/despatch`, { choices }, undefined, { timeout: 120000 });
  }

  /** 'pick-list' | 'run-sheet' | 'dockets' */
  async getLoadPdf(loadId: number, doc: 'pick-list' | 'run-sheet' | 'dockets'): Promise<Blob> {
    return apiService.get(`/nursery/loads/${loadId}/${doc}.pdf`, { responseType: 'blob' }) as unknown as Promise<Blob>;
  }

  /** The Bunnings cross dock's supplier manifest for a load: header, and one row per purchase order. */
  async getLoadManifest(loadId: number): Promise<ApiResponse<SupplierManifest>> {
    return apiService.get(`/nursery/loads/${loadId}/manifest`);
  }

  async saveLoadManifest(loadId: number, data: {
    header: SupplierManifestHeader;
    orders: { order_id: number; trays: number | null; stillages: number | null; seedling_racks: number | null; shares_with_order_id: number | null }[];
    /** Each state's own stacks, pallets and PHAC numbers. */
    states?: { state: string | null; total_stacks: number | null; total_pallets: number | null; phac_numbers: string | null }[];
    /** The organisation's usual recipients; left out, they are left alone. */
    recipients?: string[];
  }): Promise<ApiResponse<SupplierManifest>> {
    return apiService.put(`/nursery/loads/${loadId}/manifest`, data);
  }

  /** The manifest as a page to print and sign. */
  async getLoadManifestPdf(loadId: number): Promise<Blob> {
    return apiService.get(`/nursery/loads/${loadId}/manifest.pdf`, { responseType: 'blob' }) as unknown as Promise<Blob>;
  }

  /** Email the workbook; with no recipients, to the organisation's usual list. */
  async emailLoadManifest(loadId: number, recipients?: string[]): Promise<ApiResponse<SupplierManifest>> {
    return apiService.post(`/nursery/loads/${loadId}/manifest/email`, recipients ? { recipients } : {});
  }

  /** Bunnings' own workbook, filled in, for one state (its code, or 'none') when the load goes to several. */
  async getLoadManifestFile(loadId: number, state?: string): Promise<Blob> {
    const query = state ? `?state=${encodeURIComponent(state)}` : '';
    return apiService.get(`/nursery/loads/${loadId}/manifest.xlsx${query}`, { responseType: 'blob' }) as unknown as Promise<Blob>;
  }

  async createTransfersForLoad(loadId: number, toLocationId: number, scheduledDate?: string): Promise<ApiResponse<NurseryTransfer[]>> {
    return apiService.post(`/nursery/loads/${loadId}/transfers`, { to_location_id: toLocationId, scheduled_date: scheduledDate ?? null });
  }

  async getTransfers(filters?: { open?: boolean; status?: string }): Promise<ApiResponse<NurseryTransfer[]>> {
    const params = new URLSearchParams();
    if (filters?.open) params.set('open', 'true');
    if (filters?.status) params.set('status', filters.status);
    const q = params.toString() ? `?${params}` : '';
    return apiService.get(`/nursery/transfers${q}`);
  }

  async getTransfer(id: number): Promise<ApiResponse<NurseryTransfer>> {
    return apiService.get(`/nursery/transfers/${id}`);
  }

  async createTransfer(data: CreateTransferPayload): Promise<ApiResponse<NurseryTransfer>> {
    return apiService.post('/nursery/transfers', data);
  }

  async sendTransfer(id: number): Promise<ApiResponse<NurseryTransfer>> {
    return apiService.post(`/nursery/transfers/${id}/send`, {});
  }

  /** received: line id → count that arrived; a line left out arrived in full. */
  async receiveTransfer(id: number, received: Record<number, number>): Promise<ApiResponse<NurseryTransfer>> {
    return apiService.post(`/nursery/transfers/${id}/receive`, { received });
  }

  async cancelTransfer(id: number): Promise<ApiResponse<NurseryTransfer>> {
    return apiService.post(`/nursery/transfers/${id}/cancel`, {});
  }

  // Despatchable places: where orders are allocated from by themselves.
  async getDespatchPlaces(): Promise<ApiResponse<DespatchPlace[]>> {
    return apiService.get('/nursery/despatch-places');
  }

  async addDespatchPlace(data: DespatchPlacePayload): Promise<ApiResponse<DespatchPlace[]>> {
    return apiService.post('/nursery/despatch-places', data);
  }

  async removeDespatchPlace(id: number): Promise<ApiResponse<DespatchPlace[]>> {
    return apiService.delete(`/nursery/despatch-places/${id}`);
  }

  /** Allocate these orders from despatchable stock, or with none every confirmed order still short. */
  async autoAllocate(orderIds?: number[]): Promise<ApiResponse<AutoAllocateResult>> {
    return apiService.post('/nursery/auto-allocate', orderIds ? { order_ids: orderIds } : {});
  }

  /** Take back every pick on an order (zero picks included) so it can be edited again. */
  async undoOrderPicking(orderId: number): Promise<ApiResponse<{ undone: number; trees_returned: number; status: NurseryOrderStatus }>> {
    return apiService.post(`/nursery/orders/${orderId}/undo-picking`, {});
  }

  // ── Lot labels and customer notices ──

  /** An A4 sheet of lot labels, 24 to a page, each with a QR the picking page scans. */
  async getLotLabelsPdf(lotIds: number[]): Promise<Blob> {
    return apiService.get(`/nursery/lots/labels.pdf?ids=${lotIds.join(',')}`, { responseType: 'blob' }) as unknown as Promise<Blob>;
  }

  async previewLoadNotices(loadId: number, kind: NoticeKind): Promise<ApiResponse<NoticePreview>> {
    return apiService.get(`/nursery/loads/${loadId}/notices?kind=${kind}`);
  }

  async sendLoadNotices(loadId: number, kind: NoticeKind, orderIds?: number[]): Promise<ApiResponse<NoticeResult>> {
    return apiService.post(`/nursery/loads/${loadId}/notices`, { kind, ...(orderIds ? { order_ids: orderIds } : {}) });
  }

  // ── Picking ──

  async getPickLoads(): Promise<ApiResponse<PickLoadsResponse>> {
    return apiService.get('/nursery/picking/loads');
  }

  async getPickList(loadId: number): Promise<ApiResponse<PickList>> {
    return apiService.get(`/nursery/picking/loads/${loadId}`);
  }

  /** Start picking for a customer on a load, or keep the hold alive. 409 CUSTOMER_CLAIMED while someone else holds them. */
  async claimPickCustomer(loadId: number, customerKey: string, takeOver = false): Promise<ApiResponse<PickList>> {
    return apiService.post(`/nursery/picking/loads/${loadId}/customers/${encodeURIComponent(customerKey)}/claim`, takeOver ? { take_over: true } : {});
  }

  async releasePickCustomer(loadId: number, customerKey: string): Promise<ApiResponse<PickList>> {
    return apiService.delete(`/nursery/picking/loads/${loadId}/customers/${encodeURIComponent(customerKey)}/claim`);
  }

  async recordPick(allocationId: number, data: { quantity: number; short_reason?: PickShortReason | null; notes?: string | null }): Promise<ApiResponse<PickList>> {
    return apiService.post(`/nursery/picking/allocations/${allocationId}/pick`, data);
  }

  async undoPick(allocationId: number): Promise<ApiResponse<PickList>> {
    return apiService.delete(`/nursery/picking/allocations/${allocationId}/pick`);
  }

  async getPickAlternatives(allocationId: number): Promise<ApiResponse<PickAlternative[]>> {
    return apiService.get(`/nursery/picking/allocations/${allocationId}/alternatives`);
  }

  async substituteLot(allocationId: number, lotId: number, quantity: number): Promise<ApiResponse<PickList>> {
    return apiService.post(`/nursery/picking/allocations/${allocationId}/substitute`, { lot_id: lotId, quantity });
  }

  /** On a free load: how many of a line were taken from a lot. 0 takes them back. */
  async setFreePick(loadId: number, lineId: number, lotId: number, data: { quantity: number; notes?: string | null }): Promise<ApiResponse<PickList>> {
    return apiService.put(`/nursery/picking/loads/${loadId}/lines/${lineId}/lots/${lotId}`, data);
  }

  /** On a free load: no more of a line can be found. */
  async closeFreeLineShort(loadId: number, lineId: number, data: { short_reason: PickShortReason; notes?: string | null }): Promise<ApiResponse<PickList>> {
    return apiService.post(`/nursery/picking/loads/${loadId}/lines/${lineId}/short`, data);
  }

  async reopenFreeLine(loadId: number, lineId: number): Promise<ApiResponse<PickList>> {
    return apiService.delete(`/nursery/picking/loads/${loadId}/lines/${lineId}/short`);
  }

  async correctLotCount(lotId: number, data: { counted: number; new_ready_date?: string | null; reason?: string | null; load_id?: number | null }): Promise<ApiResponse<{ lot_id: number; previous_count: number; current_count: number }>> {
    return apiService.post(`/nursery/picking/lots/${lotId}/count`, data);
  }

  // ── Despatch: the planner and loads ──

  /** Backorders still to go, with the stock free for them and where each can go next. */
  async getBackorders(): Promise<ApiResponse<BackorderList>> {
    return apiService.get('/nursery/backorders');
  }

  /** Put cross dock backorders on their store's next pick day's cross dock load. */
  async putBackordersOnCrossDock(orderIds: number[]): Promise<ApiResponse<BackorderCrossDockResult[]>> {
    return apiService.post('/nursery/backorders/cross-dock', { order_ids: orderIds });
  }

  async getDespatchPlanner(from: string, to: string): Promise<ApiResponse<DespatchPlanner>> {
    return apiService.get(`/nursery/despatch/planner?${new URLSearchParams({ from, to })}`);
  }

  async getLoads(filters?: { status?: string; from?: string; to?: string; open?: boolean }): Promise<ApiResponse<NurseryLoad[]>> {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.from) params.set('from', filters.from);
    if (filters?.to) params.set('to', filters.to);
    if (filters?.open) params.set('open', 'true');
    const q = params.toString() ? `?${params}` : '';
    return apiService.get(`/nursery/loads${q}`);
  }

  async getLoad(id: number): Promise<ApiResponse<NurseryLoad>> {
    return apiService.get(`/nursery/loads/${id}`);
  }

  async createLoad(data: CreateLoadPayload): Promise<ApiResponse<NurseryLoad>> {
    return apiService.post('/nursery/loads', data);
  }

  async updateLoad(id: number, data: UpdateLoadPayload): Promise<ApiResponse<NurseryLoad>> {
    return apiService.put(`/nursery/loads/${id}`, data);
  }

  async addOrdersToLoad(id: number, orderIds: number[]): Promise<ApiResponse<NurseryLoad>> {
    return apiService.post(`/nursery/loads/${id}/orders`, { order_ids: orderIds });
  }

  async removeOrderFromLoad(id: number, orderId: number): Promise<ApiResponse<NurseryLoad>> {
    return apiService.delete(`/nursery/loads/${id}/orders/${orderId}`);
  }

  async reorderLoadStops(id: number, orderIds: number[]): Promise<ApiResponse<NurseryLoad>> {
    return apiService.put(`/nursery/loads/${id}/stops`, { order_ids: orderIds });
  }

  /** Change how a load is picked; only before picking starts. */
  async setLoadPickMode(id: number, pickMode: PickMode): Promise<ApiResponse<NurseryLoad>> {
    return apiService.put(`/nursery/loads/${id}/pick-mode`, { pick_mode: pickMode });
  }

  async getDespatchSettings(): Promise<ApiResponse<DespatchSettings>> {
    return apiService.get('/nursery/despatch-settings');
  }

  async saveDespatchSettings(data: { default_pick_mode: PickMode }): Promise<ApiResponse<DespatchSettings>> {
    return apiService.put('/nursery/despatch-settings', data);
  }

  async releaseLoad(id: number): Promise<ApiResponse<NurseryLoad>> {
    return apiService.post(`/nursery/loads/${id}/release`, {});
  }

  async unreleaseLoad(id: number): Promise<ApiResponse<NurseryLoad>> {
    return apiService.post(`/nursery/loads/${id}/unrelease`, {});
  }

  async cancelLoad(id: number): Promise<ApiResponse<NurseryLoad>> {
    return apiService.post(`/nursery/loads/${id}/cancel`, {});
  }

  /** Put orders on the day's standing loads, one per despatch method. */
  async addToDailyLoads(orderIds: number[], despatchDate: string, despatchFarmId: number): Promise<ApiResponse<NurseryLoad[]>> {
    return apiService.post('/nursery/loads/daily/orders', {
      order_ids: orderIds, despatch_date: despatchDate, despatch_farm_id: despatchFarmId,
    });
  }

  // ── Invoices ──

  async getInvoices(filters?: { status?: string }): Promise<ApiResponse<NurseryInvoice[]>> {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    const q = params.toString() ? `?${params}` : '';
    return apiService.get(`/nursery/invoices${q}`);
  }

  async getInvoice(id: number): Promise<ApiResponse<NurseryInvoice>> {
    return apiService.get(`/nursery/invoices/${id}`);
  }

  async createInvoice(data: CreateInvoicePayload): Promise<ApiResponse<NurseryInvoice>> {
    return apiService.post('/nursery/invoices', data);
  }

  async updateInvoiceStatus(id: number, status: string): Promise<ApiResponse<NurseryInvoice>> {
    return apiService.put(`/nursery/invoices/${id}/status`, { status });
  }

  // ── Licensed cultivars (stock-item picker) ──

  async getLicensedCultivars(search?: string): Promise<ApiResponse<LicensedCultivar[]>> {
    const q = search ? `?search=${encodeURIComponent(search)}` : '';
    return apiService.get(`/nursery/licensed-cultivars${q}`);
  }

  // ── Customer tiers ──

  async getCustomerTiers(includeInactive?: boolean): Promise<ApiResponse<NurseryCustomerTier[]>> {
    const q = includeInactive ? '?include_inactive=true' : '';
    return apiService.get(`/nursery/customer-tiers${q}`);
  }

  async createCustomerTier(data: CreateCustomerTierPayload): Promise<ApiResponse<NurseryCustomerTier>> {
    return apiService.post('/nursery/customer-tiers', data);
  }

  async updateCustomerTier(id: number, data: Partial<NurseryCustomerTier>): Promise<ApiResponse<NurseryCustomerTier>> {
    return apiService.put(`/nursery/customer-tiers/${id}`, data);
  }

  // ── Trade-account requests (wholesale applications) ──

  async getTradeAccountRequests(status?: string): Promise<ApiResponse<TradeAccountRequest[]>> {
    const q = status ? `?status=${encodeURIComponent(status)}` : '';
    return apiService.get(`/nursery/trade-account-requests${q}`);
  }

  async approveTradeAccountRequest(id: number, data: ReviewTradeAccountPayload): Promise<ApiResponse<TradeAccountRequest>> {
    return apiService.post(`/nursery/trade-account-requests/${id}/approve`, data);
  }

  async declineTradeAccountRequest(id: number, data: ReviewTradeAccountPayload): Promise<ApiResponse<TradeAccountRequest>> {
    return apiService.post(`/nursery/trade-account-requests/${id}/decline`, data);
  }

  // ── Delivery zones ──

  async getDeliveryZones(includeInactive?: boolean): Promise<ApiResponse<NurseryDeliveryZone[]>> {
    const q = includeInactive ? '?include_inactive=true' : '';
    return apiService.get(`/nursery/delivery-zones${q}`);
  }

  async createDeliveryZone(data: CreateDeliveryZonePayload): Promise<ApiResponse<NurseryDeliveryZone>> {
    return apiService.post('/nursery/delivery-zones', data);
  }

  async updateDeliveryZone(id: number, data: Partial<NurseryDeliveryZone>): Promise<ApiResponse<NurseryDeliveryZone>> {
    return apiService.put(`/nursery/delivery-zones/${id}`, data);
  }

  async deleteDeliveryZone(id: number): Promise<ApiResponse<void>> {
    return apiService.delete(`/nursery/delivery-zones/${id}`);
  }

  // ── Customers ──

  async getCustomers(filters?: { active?: boolean; customer_tier_id?: number; search?: string; top_level?: boolean; parent_id?: number }): Promise<ApiResponse<NurseryCustomer[]>> {
    const params = new URLSearchParams();
    if (filters?.active !== undefined) params.set('active', String(filters.active));
    if (filters?.top_level) params.set('top_level', 'true');
    if (filters?.parent_id) params.set('parent_id', String(filters.parent_id));
    if (filters?.customer_tier_id) params.set('customer_tier_id', String(filters.customer_tier_id));
    if (filters?.search) params.set('search', filters.search);
    const q = params.toString() ? `?${params}` : '';
    return apiService.get(`/nursery/customers${q}`);
  }

  async getCustomer(id: number): Promise<ApiResponse<NurseryCustomer>> {
    return apiService.get(`/nursery/customers/${id}`);
  }

  /** The walk-in customer for counter sales, made the first time it is asked for. */
  async getWalkInCustomer(): Promise<ApiResponse<NurseryCustomer>> {
    return apiService.post('/nursery/customers/walk-in', {});
  }

  async createCustomer(data: CreateCustomerPayload): Promise<ApiResponse<NurseryCustomer>> {
    return apiService.post('/nursery/customers', data);
  }

  async updateCustomer(id: number, data: Partial<CreateCustomerPayload> & { active?: boolean }): Promise<ApiResponse<NurseryCustomer>> {
    return apiService.put(`/nursery/customers/${id}`, data);
  }

  async deleteCustomer(id: number): Promise<ApiResponse<void>> {
    return apiService.delete(`/nursery/customers/${id}`);
  }

  // ── Price lists ──

  async getPriceLists(filters?: { status?: string; customer_tier_id?: number; customer_id?: number }): Promise<ApiResponse<NurseryPriceList[]>> {
    const params = new URLSearchParams();
    if (filters?.customer_id) params.set('customer_id', String(filters.customer_id));
    if (filters?.status) params.set('status', filters.status);
    if (filters?.customer_tier_id) params.set('customer_tier_id', String(filters.customer_tier_id));
    const q = params.toString() ? `?${params}` : '';
    return apiService.get(`/nursery/price-lists${q}`);
  }

  async getPriceList(id: number): Promise<ApiResponse<NurseryPriceList>> {
    return apiService.get(`/nursery/price-lists/${id}`);
  }

  async createPriceList(data: CreatePriceListPayload): Promise<ApiResponse<NurseryPriceList>> {
    return apiService.post('/nursery/price-lists', data);
  }

  async updatePriceList(id: number, data: Partial<CreatePriceListPayload>): Promise<ApiResponse<NurseryPriceList>> {
    return apiService.put(`/nursery/price-lists/${id}`, data);
  }

  async deletePriceList(id: number): Promise<ApiResponse<void>> {
    return apiService.delete(`/nursery/price-lists/${id}`);
  }

  /** Make a list the one every customer not given another is priced from. */
  async setDefaultPriceList(id: number): Promise<ApiResponse<{ id: number; is_default: true }>> {
    return apiService.put(`/nursery/price-lists/${id}/default`, {});
  }

  /** Price-book price for a stock item, or `{ productId }` for an umbrella product. */
  async getPriceQuote(item: number | { productId: number }, quantity?: number, customerId?: number): Promise<ApiResponse<PriceQuote>> {
    const params = typeof item === 'number'
      ? new URLSearchParams({ stock_item_id: String(item) })
      : new URLSearchParams({ product_id: String(item.productId) });
    if (quantity) params.set('quantity', String(quantity));
    if (customerId) params.set('customer_id', String(customerId));
    return apiService.get(`/nursery/price-quote?${params}`);
  }

  // ── Tree-type readiness rules ──

  async updateTreeType(id: number, data: TreeTypeUpdatePayload): Promise<ApiResponse<NurseryTreeType>> {
    return apiService.put(`/nursery/tree-types/${id}`, data);
  }

  // ── Grades and what a lower grade costs ──

  async getTreeTypeGrades(): Promise<ApiResponse<TreeTypeGradesResponse>> {
    return apiService.get('/nursery/tree-types/grades');
  }

  async setTreeTypeGrades(
    treeTypeId: number,
    grades: Omit<TreeTypeGrade, 'id' | 'tree_type_id'>[],
  ): Promise<ApiResponse<TreeTypeGrade[]>> {
    return apiService.put(`/nursery/tree-types/${treeTypeId}/grades`, { grades });
  }

  async setGradeDiscounts(discounts: GradeDiscount[]): Promise<ApiResponse<GradeDiscount[]>> {
    return apiService.put('/nursery/grade-discounts', { discounts });
  }

  // ── Forecast: what the growing crop is expected to give ──

  async getForecast(filters?: { season?: number; farm_id?: number; stock_type?: 'B' | 'P' }): Promise<ApiResponse<ForecastResponse>> {
    const params = new URLSearchParams();
    if (filters?.season) params.set('season', String(filters.season));
    if (filters?.farm_id) params.set('farm_id', String(filters.farm_id));
    if (filters?.stock_type) params.set('stock_type', filters.stock_type);
    const q = params.toString();
    return apiService.get(`/nursery/forecast${q ? `?${q}` : ''}`);
  }

  // ── Locations ──

  async getLocations(filters?: {
    location_type?: string; block_id?: number; farm_id?: number; active?: boolean;
  }): Promise<ApiResponse<NurseryLocation[]>> {
    const params = new URLSearchParams();
    if (filters?.location_type) params.set('location_type', filters.location_type);
    if (filters?.block_id) params.set('block_id', String(filters.block_id));
    if (filters?.farm_id) params.set('farm_id', String(filters.farm_id));
    if (filters?.active !== undefined) params.set('active', String(filters.active));
    const q = params.toString() ? `?${params}` : '';
    return apiService.get(`/nursery/locations${q}`);
  }

  async getLocation(id: number): Promise<ApiResponse<NurseryLocation>> {
    return apiService.get(`/nursery/locations/${id}`);
  }

  async createLocation(data: CreateLocationPayload): Promise<ApiResponse<NurseryLocation>> {
    return apiService.post('/nursery/locations', data);
  }

  async updateLocation(id: number, data: Partial<NurseryLocation>): Promise<ApiResponse<NurseryLocation>> {
    return apiService.put(`/nursery/locations/${id}`, data);
  }

  async deleteLocation(id: number): Promise<ApiResponse<void>> {
    return apiService.delete(`/nursery/locations/${id}`);
  }

  async createRowSlots(data: CreateRowSlotsPayload): Promise<ApiResponse<NurseryLocation[]>> {
    return apiService.post('/nursery/row-slots', data);
  }

  /** Slots with lots, a transfer or a despatch area on them are kept and counted. */
  async deleteRowSlots(data: { block_id: number; ids: number[] }): Promise<ApiResponse<{ deleted: number; kept: number }>> {
    return apiService.post('/nursery/row-slots/delete', data);
  }

  async calculateLocationCapacity(id: number, spacing?: { row_spacing_meters?: number; plant_spacing_meters?: number }): Promise<ApiResponse<{ capacity: number | null }>> {
    return apiService.post(`/nursery/locations/${id}/calculate-capacity`, spacing || {});
  }

  // ── Block layouts (row heading / origin) ──

  async getBlockLayouts(): Promise<ApiResponse<NurseryBlockLayout[]>> {
    return apiService.get('/nursery/block-layouts');
  }

  async getBlockLayout(blockId: number): Promise<ApiResponse<NurseryBlockLayout>> {
    return apiService.get(`/nursery/block-layouts/${blockId}`);
  }

  async upsertBlockLayout(blockId: number, data: BlockLayoutPayload): Promise<ApiResponse<NurseryBlockLayout>> {
    return apiService.put(`/nursery/block-layouts/${blockId}`, data);
  }

  // ── Lots ──

  async getLots(filters?: {
    status?: string; stage?: string; cultivar_id?: number; location_id?: number; tree_type_id?: number;
  } & PlaceFilter): Promise<ApiResponse<NurseryLot[]>> {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.stage) params.set('stage', filters.stage);
    if (filters?.cultivar_id) params.set('cultivar_id', String(filters.cultivar_id));
    if (filters?.location_id) params.set('location_id', String(filters.location_id));
    if (filters?.tree_type_id) params.set('tree_type_id', String(filters.tree_type_id));
    if (filters?.farm_id) params.set('farm_id', String(filters.farm_id));
    if (filters?.block_id) params.set('block_id', String(filters.block_id));
    if (filters?.row_number) params.set('row_number', String(filters.row_number));
    if (filters?.row_side) params.set('row_side', filters.row_side);
    const q = params.toString() ? `?${params}` : '';
    return apiService.get(`/nursery/lots${q}`);
  }

  async getLot(id: number): Promise<ApiResponse<NurseryLot>> {
    return apiService.get(`/nursery/lots/${id}`);
  }

  async getLotLineage(id: number): Promise<ApiResponse<NurseryLotLineage>> {
    return apiService.get(`/nursery/lots/${id}/lineage`);
  }

  /** The sprays this lot has been under, newest first. Any nursery seat. */
  async getLotSprays(id: number): Promise<ApiResponse<LotSprayHistoryEntry[]>> {
    return apiService.get(`/nursery/lots/${id}/sprays`);
  }

  async createLot(data: CreateLotPayload): Promise<ApiResponse<NurseryLot>> {
    return apiService.post('/nursery/lots', data);
  }

  async updateLot(id: number, data: Partial<CreateLotPayload> & { status?: string; current_count?: number }): Promise<ApiResponse<NurseryLot>> {
    return apiService.put(`/nursery/lots/${id}`, data);
  }

  /** Move lots to a stage (ready to sell above all). Any nursery operator seat. */
  async changeLotStage(data: { lot_ids: number[]; stage: LotStage; ready_date?: string | null; date?: string; notes?: string | null }): Promise<ApiResponse<LotChangeResult>> {
    return apiService.post('/nursery/lots/stage', data);
  }

  /** Take lots out of stock, or put archived ones back. Nursery manager or admin. */
  async changeLotStatus(data: { lot_ids: number[]; action: 'archive' | 'restore'; date?: string; notes?: string | null }): Promise<ApiResponse<LotChangeResult>> {
    return apiService.post('/nursery/lots/status', data);
  }

  // ── Movements ──

  async getMovements(filters?: { lot_id?: number; event_type?: string }): Promise<ApiResponse<NurseryMovement[]>> {
    const params = new URLSearchParams();
    if (filters?.lot_id) params.set('lot_id', String(filters.lot_id));
    if (filters?.event_type) params.set('event_type', filters.event_type);
    const q = params.toString() ? `?${params}` : '';
    return apiService.get(`/nursery/movements${q}`);
  }

  async applyMovement(data: ApplyMovementPayload): Promise<ApiResponse<NurseryMovement>> {
    return apiService.post('/nursery/movements', data);
  }

  // ── Production board (what's ready) ──

  async getProductionBoard(filters?: ProductionBoardFilters): Promise<ApiResponse<NurseryLot[]>> {
    const params = new URLSearchParams();
    Object.entries(filters || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
    });
    const q = params.toString() ? `?${params}` : '';
    return apiService.get(`/nursery/production${q}`);
  }

  // ── Production runs ──

  async getProductionRuns(filters?: { status?: string; run_type?: string }): Promise<ApiResponse<NurseryProductionRun[]>> {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.run_type) params.set('run_type', filters.run_type);
    const q = params.toString() ? `?${params}` : '';
    return apiService.get(`/nursery/production-runs${q}`);
  }

  async getProductionRun(id: number): Promise<ApiResponse<NurseryProductionRun>> {
    return apiService.get(`/nursery/production-runs/${id}`);
  }

  async createProductionRun(data: CreateProductionRunPayload): Promise<ApiResponse<NurseryProductionRun>> {
    return apiService.post('/nursery/production-runs', data);
  }

  async addLotsToRun(id: number, lots: Array<number | { lot_id: number; quantity?: number | null }>): Promise<ApiResponse<NurseryProductionRun>> {
    return apiService.post(`/nursery/production-runs/${id}/lots`, { lots });
  }

  async completeProductionRun(id: number): Promise<ApiResponse<NurseryProductionRun>> {
    return apiService.post(`/nursery/production-runs/${id}/complete`, {});
  }

  // ── Inventory + dashboard (lot-based) ──

  async getInventory(): Promise<ApiResponse<NurseryInventoryRow[]>> {
    return apiService.get('/nursery/inventory');
  }

  async refreshInventory(): Promise<ApiResponse<NurseryInventoryRow[]>> {
    return apiService.post('/nursery/inventory/refresh', {});
  }

  async getDashboard(): Promise<ApiResponse<NurseryDashboardStatsV2>> {
    return apiService.get('/nursery/dashboard');
  }
}

const nurseryService = new NurseryService();
export default nurseryService;
